import { updatedEntities, denormalisedEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { LISTING_STATE_DRAFT } from '../../util/types';

// ================ Action types ================ //

export const FETCH_PROJECTS_REQUEST = 'app/ManageProjectListingsPage/FETCH_PROJECTS_REQUEST';
export const FETCH_PROJECTS_SUCCESS = 'app/ManageProjectListingsPage/FETCH_PROJECTS_SUCCESS';
export const FETCH_PROJECTS_ERROR = 'app/ManageProjectListingsPage/FETCH_PROJECTS_ERROR';

export const OPEN_PROJECT_REQUEST = 'app/ManageProjectListingsPage/OPEN_PROJECT_REQUEST';
export const OPEN_PROJECT_SUCCESS = 'app/ManageProjectListingsPage/OPEN_PROJECT_SUCCESS';
export const OPEN_PROJECT_ERROR = 'app/ManageProjectListingsPage/OPEN_PROJECT_ERROR';

export const CLOSE_PROJECT_REQUEST = 'app/ManageProjectListingsPage/CLOSE_PROJECT_REQUEST';
export const CLOSE_PROJECT_SUCCESS = 'app/ManageProjectListingsPage/CLOSE_PROJECT_SUCCESS';
export const CLOSE_PROJECT_ERROR = 'app/ManageProjectListingsPage/CLOSE_PROJECT_ERROR';

export const DISCARD_DRAFT_REQUEST = 'app/ManageProjectListingsPage/DISCARD_DRAFT_REQUEST';
export const DISCARD_DRAFT_SUCCESS = 'app/ManageProjectListingsPage/DISCARD_DRAFT_SUCCESS';
export const DISCARD_DRAFT_ERROR = 'app/ManageProjectListingsPage/DISCARD_DRAFT_ERROR';

export const HIDE_LISTING_REQUEST = 'app/ManageProjectListingsPage/HIDE_LISTING_REQUEST';
export const SHOW_LISTING_REQUEST = 'app/ManageProjectListingsPage/SHOW_LISTING_REQUEST';

export const ADD_OWN_ENTITIES = 'app/ManageProjectListingsPage/ADD_OWN_ENTITIES';

// ================ Reducer ================ //

const initialState = {
  pagination: null,
  queryParams: null,
  queryInProgress: false,
  queryListingsError: null,
  currentPageResultIds: [],
  ownEntities: {},
  openingListing: null,
  openingListingError: null,
  closingListing: null,
  closingListingError: null,
  discardingDraft: null,
  discardingDraftError: null,
  hideListing: null,
};

const resultIds = data => data.data.map(l => l.id);

const merge = (state, sdkResponse) => {
  const apiResponse = sdkResponse.data;
  return {
    ...state,
    ownEntities: updatedEntities({ ...state.ownEntities }, apiResponse),
  };
};

const updateListingAttributes = (state, listingEntity) => {
  const oldListing = state.ownEntities.ownListing[listingEntity.id.uuid];
  const updatedListing = { ...oldListing, attributes: listingEntity.attributes };
  const ownListingEntities = {
    ...state.ownEntities.ownListing,
    [listingEntity.id.uuid]: updatedListing,
  };
  return {
    ...state,
    ownEntities: { ...state.ownEntities, ownListing: ownListingEntities },
  };
};

const manageListingsPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case FETCH_PROJECTS_REQUEST:
      return {
        ...state,
        queryParams: payload.queryParams,
        queryInProgress: true,
        queryListingsError: null,
        currentPageResultIds: [],
      };
    case FETCH_PROJECTS_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        queryInProgress: false,
      };
    case FETCH_PROJECTS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, queryInProgress: false, queryListingsError: payload };

    case OPEN_PROJECT_REQUEST:
      return {
        ...state,
        openingListing: payload.listingId,
        openingListingError: null,
      };
    case OPEN_PROJECT_SUCCESS:
      return {
        ...updateListingAttributes(state, payload.data),
        openingListing: null,
      };
    case OPEN_PROJECT_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        openingListing: null,
        openingListingError: {
          listingId: state.openingListing,
          error: payload,
        },
      };
    }

    case CLOSE_PROJECT_REQUEST:
      return {
        ...state,
        closingListing: payload.listingId,
        closingListingError: null,
      };
    case CLOSE_PROJECT_SUCCESS:
      return {
        ...updateListingAttributes(state, payload.data),
        closingListing: null,
      };
    case CLOSE_PROJECT_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        closingListing: null,
        closingListingError: {
          listingId: state.closingListing,
          error: payload,
        },
      };
    }

    case DISCARD_DRAFT_REQUEST:
      return {
        ...state,
        discardingDraft: payload.listingId,
        discardingDraftError: null,
      };
    case DISCARD_DRAFT_SUCCESS: {
      // The listing no longer exists, so drop it from the page and the entity store.
      const { [payload.listingId.uuid]: discarded, ...ownListing } =
        state.ownEntities.ownListing || {};
      return {
        ...state,
        currentPageResultIds: state.currentPageResultIds.filter(
          id => id.uuid !== payload.listingId.uuid
        ),
        ownEntities: { ...state.ownEntities, ownListing },
        discardingDraft: null,
      };
    }
    case DISCARD_DRAFT_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        discardingDraft: null,
        discardingDraftError: {
          listingId: state.discardingDraft,
          error: payload,
        },
      };
    }

    case HIDE_LISTING_REQUEST: {
      return { ...updateListingAttributes(state, payload.data), hideListing: true };
    }

    case SHOW_LISTING_REQUEST: {
      return { ...updateListingAttributes(state, payload.data), hideListing: false };
    }

    case ADD_OWN_ENTITIES:
      return merge(state, payload);

    default:
      return state;
  }
};

export default manageListingsPageReducer;

// ================ Selectors ================ //

/**
 * Get the denormalised own listing entities with the given IDs
 *
 * @param {Object} state the full Redux store
 * @param {Array<UUID>} listingIds listing IDs to select from the store
 */
export const getOwnListingsById = (state, listingIds) => {
  const { ownEntities } = state.ManageProjectListingsPage;
  const resources = listingIds.map(id => ({
    id,
    type: 'ownListing',
  }));
  const throwIfNotFound = false;
  return denormalisedEntities(ownEntities, resources, throwIfNotFound);
};

// ================ Action creators ================ //

// This works the same way as addMarketplaceEntities,
// but we don't want to mix own listings with searched listings
// (own listings data contains different info - e.g. exact location etc.)
export const addOwnEntities = sdkResponse => ({
  type: ADD_OWN_ENTITIES,
  payload: sdkResponse,
});

export const openListingRequest = listingId => ({
  type: OPEN_PROJECT_REQUEST,
  payload: { listingId },
});

export const openListingSuccess = response => ({
  type: OPEN_PROJECT_SUCCESS,
  payload: response.data,
});

export const openListingError = e => ({
  type: OPEN_PROJECT_ERROR,
  error: true,
  payload: e,
});

export const closeListingRequest = listingId => ({
  type: CLOSE_PROJECT_REQUEST,
  payload: { listingId },
});

export const closeListingSuccess = response => ({
  type: CLOSE_PROJECT_SUCCESS,
  payload: response.data,
});

export const closeListingError = e => ({
  type: CLOSE_PROJECT_ERROR,
  error: true,
  payload: e,
});

export const discardDraftRequest = listingId => ({
  type: DISCARD_DRAFT_REQUEST,
  payload: { listingId },
});

export const discardDraftSuccess = listingId => ({
  type: DISCARD_DRAFT_SUCCESS,
  payload: { listingId },
});

export const discardDraftError = e => ({
  type: DISCARD_DRAFT_ERROR,
  error: true,
  payload: e,
});

export const hideListingRequest = response => ({
  type: HIDE_LISTING_REQUEST,
  payload: response.data,
});

export const showListingRequest = response => ({
  type: SHOW_LISTING_REQUEST,
  payload: response.data,
});

export const queryListingsRequest = queryParams => ({
  type: FETCH_PROJECTS_REQUEST,
  payload: { queryParams },
});

export const queryListingsSuccess = response => ({
  type: FETCH_PROJECTS_SUCCESS,
  payload: { data: response.data },
});

export const queryListingsError = e => ({
  type: FETCH_PROJECTS_ERROR,
  error: true,
  payload: e,
});

// Throwing error for new (loadData may need that info)
export const queryOwnListings = queryParams => (dispatch, getState, sdk) => {
  dispatch(queryListingsRequest(queryParams));

  const { perPage, ...rest } = queryParams;
  const params = { ...rest, per_page: perPage };

  return sdk.ownListings
    .query(params)
    .then(response => {
      dispatch(addOwnEntities(response));
      dispatch(queryListingsSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(queryListingsError(storableError(e)));
      throw e;
    });
};

export const closeListing = listingId => (dispatch, getState, sdk) => {
  dispatch(closeListingRequest(listingId));

  return sdk.ownListings
    .close({ id: listingId }, { expand: true })
    .then(response => {
      dispatch(closeListingSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(closeListingError(storableError(e)));
    });
};

export const openListing = listingId => (dispatch, getState, sdk) => {
  dispatch(openListingRequest(listingId));

  return sdk.ownListings
    .open({ id: listingId }, { expand: true })
    .then(response => {
      dispatch(openListingSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(openListingError(storableError(e)));
    });
};

// Only listings that have never been published can be discarded. A Sharetribe
// listing can't return to the draft state once published, so `draft` means it
// was never published. The API enforces the same rule: discard_draft responds
// 409 listing-invalid-state for pendingApproval, published and closed listings.
export const discardDraft = listingId => (dispatch, getState, sdk) => {
  dispatch(discardDraftRequest(listingId));

  const [listing] = getOwnListingsById(getState(), [listingId]);
  if (!listing || listing.attributes.state !== LISTING_STATE_DRAFT) {
    dispatch(discardDraftError(storableError(new Error('Only draft listings can be discarded'))));
    return Promise.resolve();
  }

  return sdk.ownListings
    .discardDraft({ id: listingId })
    .then(response => {
      dispatch(discardDraftSuccess(listingId));
      return response;
    })
    .catch(e => {
      dispatch(discardDraftError(storableError(e)));
    });
};

export const hideListing = listingId => async (dispatch, getState, sdk) => {
  const response = await sdk.ownListings.update(
    { id: listingId, publicData: { hidden: true } },
    { expand: true }
  );
  dispatch(hideListingRequest(response));
  return response;
};

export const showListing = listingId => async (dispatch, getState, sdk) => {
  const response = await sdk.ownListings.update(
    { id: listingId, publicData: { hidden: false } },
    { expand: true }
  );
  dispatch(showListingRequest(response));
  return response;
};
